/**
 * ChatPanel — Collapsible right panel for AI assistant.
 *
 * Contains:
 * - Punch list (case action items)
 * - Chat message thread
 * - Text input for conversation
 *
 * The chat agent can programmatically push messages and
 * trigger main panel view changes via the onViewChange callback.
 *
 * Props:
 *   open: boolean — whether panel is visible
 *   onClose: () => void — close the panel
 *   punchListItems: Array<{ item_id, title, status, priority, scope, child_count, resolved_child_count }> — punch list items
 *   punchListProgress: { total, resolved, remaining, by_status } — progress summary
 *   punchListBreadcrumb: Array<{ itemId, title }> — breadcrumb trail for hierarchical navigation
 *   messages: Array<{ id, role, content, actions? }> — chat messages
 *   onSendMessage: (text) => void — send a message
 *   onPunchListToggle: (itemId, scope) => void — resolve/unresolve a punch list item
 *   onPunchListDrillDown: (itemId) => void — drill into a punch list item's children
 *   onPunchListNavigate: (itemId|null) => void — navigate breadcrumb (null = top level)
 *   onViewChange: (viewConfig) => void — chat agent requests a view change
 *   config: object — optional config overrides
 */

import React, { useState, useRef, useEffect } from 'react';
import styles from './ChatPanel.module.css';

/* ---- Priority badge ---- */
const priorityColors = {
  CRITICAL: 'var(--color-error-500, #dc2626)',
  HIGH: 'var(--color-warning-500, #f59e0b)',
  MEDIUM: 'var(--color-accent-500, #0d9488)',
  LOW: 'var(--color-neutral-400, #9ca3af)',
};

/* ---- Punch List ---- */
function PunchList({ items = [], progress, breadcrumb = [], onToggle, onDrillDown, onNavigateBreadcrumb }) {
  if (items.length === 0 && breadcrumb.length === 0) return null;

  const resolved = progress?.resolved || 0;
  const total = progress?.total || items.length;
  const remaining = progress?.remaining ?? (total - resolved);

  return (
    <div className={styles.punchList}>
      <div className={styles.punchListTitle}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="8" height="18" x="3" y="3" rx="1"/><path d="M7 3v18"/>
          <path d="M20.4 18.9c.2.5-.1 1.1-.6 1.3l-1.9.7c-.5.2-1.1-.1-1.3-.6L11.1 5.1c-.2-.5.1-1.1.6-1.3l1.9-.7c.5-.2 1.1.1 1.3.6Z"/>
        </svg>
        Punch List
        {total > 0 && (
          <span className={styles.punchProgress}>{resolved}/{total} resolved</span>
        )}
      </div>

      {/* Breadcrumb trail for hierarchical navigation */}
      {breadcrumb.length > 0 && (
        <div className={styles.punchBreadcrumb}>
          <button
            className={styles.punchBreadcrumbItem}
            onClick={() => onNavigateBreadcrumb && onNavigateBreadcrumb(null)}
          >
            Top
          </button>
          {breadcrumb.map((crumb, idx) => (
            <React.Fragment key={crumb.itemId}>
              <span className={styles.punchBreadcrumbSep}>&rsaquo;</span>
              <button
                className={styles.punchBreadcrumbItem}
                onClick={() => onNavigateBreadcrumb && onNavigateBreadcrumb(crumb.itemId)}
              >
                {crumb.title.length > 25 ? crumb.title.slice(0, 25) + '...' : crumb.title}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {items.map((item) => {
        const isDone = item.status === 'RESOLVED' || item.status === 'DISMISSED';
        return (
          <div
            key={item.item_id}
            className={`${styles.punchItem} ${isDone ? styles.punchItemDone : ''}`}
          >
            <button
              className={styles.punchCheck}
              onClick={() => onToggle && onToggle(item.item_id, item.scope)}
              title={isDone ? 'Resolved' : 'Mark resolved'}
              style={{ borderColor: isDone ? 'var(--color-success-500, #22c55e)' : priorityColors[item.priority] || priorityColors.MEDIUM }}
            />
            <span
              className={styles.punchItemText}
              onClick={() => item.child_count > 0 && onDrillDown && onDrillDown(item.item_id)}
              style={{ cursor: item.child_count > 0 ? 'pointer' : 'default' }}
            >
              {item.title}
              {item.child_count > 0 && (
                <span className={styles.punchChildBadge}>
                  {item.resolved_child_count}/{item.child_count}
                </span>
              )}
            </span>
          </div>
        );
      })}

      {items.length === 0 && breadcrumb.length > 0 && (
        <div className={styles.punchEmpty}>No items at this level</div>
      )}
    </div>
  );
}

/* ---- Chat Message ---- */
function ChatMessage({ message }) {
  const isAI = message.role === 'assistant';

  return (
    <div className={styles.message}>
      <div className={`${styles.msgAvatar} ${isAI ? styles.msgAvatarAI : styles.msgAvatarUser}`}>
        {isAI ? 'AI' : message.initials || 'U'}
      </div>
      <div className={`${styles.msgBubble} ${isAI ? styles.msgBubbleAI : ''}`}>
        <div dangerouslySetInnerHTML={{ __html: message.content }} />
        {message.actions && message.actions.map((action, idx) => (
          <button
            key={idx}
            className={styles.msgAction}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---- Main ChatPanel ---- */
function ChatPanel({
  open, onClose,
  punchListItems, punchListProgress, punchListBreadcrumb,
  messages = [],
  onSendMessage, onPunchListToggle, onPunchListDrillDown, onPunchListNavigate,
}) {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text) return;
    if (onSendMessage) {
      onSendMessage(text);
    }
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e) => {
    setInputValue(e.target.value);
    // Auto-resize textarea
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  return (
    <aside className={`${styles.panel} ${open ? styles.panelOpen : ''}`}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>LIVI</h2>
        <button
          className={styles.headerBtn}
          onClick={onClose}
          title="Close"
          aria-label="Close LIVI"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="3" rx="2"/>
            <path d="M15 3v18"/>
          </svg>
        </button>
      </div>

      {/* Punch List */}
      <PunchList
        items={punchListItems}
        progress={punchListProgress}
        breadcrumb={punchListBreadcrumb}
        onToggle={onPunchListToggle}
        onDrillDown={onPunchListDrillDown}
        onNavigateBreadcrumb={onPunchListNavigate}
      />

      {/* Messages */}
      <div className={styles.messages}>
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.inputArea}>
        <div className={styles.inputWrapper}>
          <textarea
            ref={textareaRef}
            className={styles.input}
            placeholder="Ask about this case..."
            value={inputValue}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={!inputValue.trim()}
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/>
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );
}

export default ChatPanel;
